import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, Polyline, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Users, Clock, Route as RouteIcon, Truck, Footprints, AlertTriangle, Loader2, Flag } from "lucide-react";
import { ROUTE_COLORS } from "@/components/app/CompanyRoutesMapModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stopIcon = (n, color) => L.divIcon({
  className: "custom-marker",
  html: `<div style="background:${color};color:#fff;width:24px;height:24px;border-radius:50%;border:2.5px solid white;box-shadow:0 1px 4px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:800;font-family:monospace;">${n}</div>`,
  iconSize: [24, 24], iconAnchor: [12, 12],
});
const destIcon = L.divIcon({
  className: "custom-marker",
  html: `<div style="background:#0A0A0A;color:#fff;padding:3px 8px;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,.4);font-size:10px;font-weight:800;letter-spacing:1px;white-space:nowrap;">HEDEF</div>`,
  iconSize: [52, 22], iconAnchor: [26, 11],
});

export default function RouteSharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/route-share/${token}`)
      .then((res) => setData(res.data))
      .catch((e) => setError(e?.response?.data?.detail || "Paylaşım yüklenemedi"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="w-8 h-8 text-white animate-spin" /></div>;
  if (error) return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6">
      <div className="text-center" data-testid="route-share-error">
        <AlertTriangle className="w-10 h-10 text-[#F59E0B] mx-auto mb-4" />
        <div className="text-white font-bold text-lg mb-2">Link Geçersiz</div>
        <div className="text-neutral-400 text-sm">{error}</div>
      </div>
    </div>
  );

  const routes = data.routes.filter((r) => (r.stops || []).length > 0);
  const noMapRoutes = data.routes.filter((r) => (r.stops || []).length === 0);
  const totalPeople = data.routes.reduce((a, r) => a + r.personnel_count, 0);
  const points = routes.flatMap((r) => [...r.stops.map((s) => [s.lat, s.lng]), ...(r.dest ? [[r.dest.lat, r.dest.lng]] : [])]);
  const bounds = points.length > 1 ? L.latLngBounds(points) : null;
  const dests = [];
  routes.forEach((r) => {
    if (r.dest && !dests.some((d) => Math.abs(d.lat - r.dest.lat) < 1e-6 && Math.abs(d.lng - r.dest.lng) < 1e-6)) dests.push(r.dest);
  });

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="route-share-page">
      <div className="bg-[#0A0A0A] text-white px-4 pt-6 pb-5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400">Keskintur — Güzergah Planı</div>
        <div className="text-xl font-bold mt-1">{data.company_name}</div>
        <div className="text-xs text-neutral-400 mt-0.5">
          <span className="font-semibold text-white">{data.routes.length}</span> güzergah · <span className="font-semibold text-white">{totalPeople}</span> personel
        </div>
      </div>

      {points.length > 0 && (
        <div style={{ height: "46vh", minHeight: 300 }}>
          <MapContainer bounds={bounds || undefined} center={points[0]} zoom={12} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {routes.map((r, i) => {
              const color = ROUTE_COLORS[i % ROUTE_COLORS.length];
              const line = (r.geometry || []).map(([lng, lat]) => [lat, lng]);
              return (
                <div key={r.id}>
                  {line.length > 1 && <Polyline positions={line} pathOptions={{ color, weight: 4, opacity: 0.75 }} />}
                  {r.stops.map((s, si) => (
                    <Marker key={si} position={[s.lat, s.lng]} icon={stopIcon(si + 1, color)}>
                      <Tooltip direction="top" offset={[0, -10]}>
                        <span style={{ fontSize: 10, fontWeight: 700 }}>{(s.personnel || []).map((p) => p.name).join(", ").slice(0, 60)}</span>
                      </Tooltip>
                      <Popup>
                        <div className="text-xs" style={{ minWidth: 160 }}>
                          <div className="font-bold mb-1" style={{ color }}>Durak {si + 1}</div>
                          {(s.personnel || []).map((p) => (
                            <div key={p.id} className="flex justify-between gap-3 py-0.5">
                              <span>{p.name}</span><span className="text-neutral-500">{p.walk_m} m</span>
                            </div>
                          ))}
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </div>
              );
            })}
            {dests.map((d, i) => <Marker key={i} position={[d.lat, d.lng]} icon={destIcon} />)}
          </MapContainer>
        </div>
      )}

      <div className="p-3 space-y-3 max-w-2xl mx-auto pb-10">
        {[...routes, ...noMapRoutes].map((r, i) => {
          const color = i < routes.length ? ROUTE_COLORS[i % ROUTE_COLORS.length] : "#9CA3AF";
          return (
            <div key={r.id} className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden" data-testid={`share-route-${r.id}`}>
              <div className="px-4 py-3" style={{ borderTop: `3px solid ${color}` }}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 text-white ${r.direction === "pickup" ? "bg-[#16A34A]" : "bg-[#0020C2]"}`}>
                    {r.direction === "pickup" ? "ALIM" : "BIRAKMA"}
                  </span>
                  {r.shift && <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-neutral-100">{r.shift}</span>}
                  <span className="font-bold text-sm">{r.name}</span>
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-neutral-600 flex-wrap">
                  <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5 text-neutral-400" /><b className="num">{r.personnel_count}</b></span>
                  {r.estimated_km > 0 && <span className="flex items-center gap-1"><RouteIcon className="w-3.5 h-3.5" style={{ color }} /><b className="num">{r.estimated_km}</b> km</span>}
                  {r.duration_min > 0 && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" style={{ color }} />~<b className="num">{r.duration_min}</b> dk</span>}
                  {r.vehicle_plate && <span className="flex items-center gap-1"><Truck className="w-3.5 h-3.5 text-neutral-400" />{r.vehicle_plate}</span>}
                  {r.driver_name && <span className="text-neutral-500">{r.driver_name}</span>}
                </div>
              </div>
              <div className="border-t border-[#F5F5F5] px-4 py-2">
                {(r.stops || []).map((s, si) =>
                  (s.personnel || []).map((p) => (
                    <div key={p.id} className="flex items-center justify-between text-xs py-1 border-b border-[#FAFAFA] last:border-0">
                      <span><span className="num text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 mr-2" style={{ background: color }}>{si + 1}</span>{p.name}</span>
                      <span className="flex items-center gap-2 text-neutral-500 shrink-0">
                        {p.pickup_time && <b className="num text-[#0A0A0A]">{p.pickup_time}</b>}
                        {p.walk_m > 0 && <span className="flex items-center gap-0.5 num text-[11px]"><Footprints className="w-3 h-3" />{p.walk_m} m</span>}
                      </span>
                    </div>
                  ))
                )}
                {(r.dest?.label) && (
                  <div className="flex items-center gap-1.5 text-xs py-1.5 text-neutral-600">
                    <Flag className="w-3 h-3 text-[#DC2626]" /> <b>Varış:</b> {r.dest.label}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div className="text-center text-[10px] text-neutral-400 uppercase tracking-widest pt-4">Keskintur Portal</div>
      </div>
    </div>
  );
}
